import { Tooltip } from "antd";

import { AGREEMENT_LABEL, AGREEMENT_STYLE } from "./lineage";
import type { LineageClusterValue, LineageFieldCluster } from "./lineage.types";

interface FieldAgreementListProps {
  clusters: LineageFieldCluster[];
}

function ValueChip({
  value,
  agreement,
}: {
  value: LineageClusterValue;
  agreement: LineageFieldCluster["agreement"];
}) {
  const emphasis = value.is_plurality
    ? "border-[#86EFAC] bg-[#F0FDF4]"
    : agreement === "conflict"
      ? "border-[#FCA5A5] bg-[#FEF2F2]"
      : "border-[#FCD34D] bg-[#FFFBEB]";
  return (
    <Tooltip
      title={`Reported by ${value.count} record${value.count === 1 ? "" : "s"} from ${value.sources.join(", ")}.`}
    >
      <span
        className={`inline-flex cursor-help items-baseline gap-1.5 rounded border px-2 py-1 ${emphasis}`}
      >
        <span className="text-[12px] text-[#1F2933]">{value.value}</span>
        <span className="text-[11px] font-semibold text-[#2B4752] tabular-nums">
          ×{value.count}
        </span>
        <span className="text-[11px] text-[#556C75]">
          {value.sources.join(" · ")}
        </span>
      </span>
    </Tooltip>
  );
}

function ClusterRow({ cluster }: { cluster: LineageFieldCluster }) {
  const style = AGREEMENT_STYLE[cluster.agreement];
  return (
    <div className="flex items-start gap-3 border-t border-[#F0F2F4] py-2 first:border-t-0">
      <div className="flex w-[150px] shrink-0 flex-col gap-1">
        <span className="text-[12px] font-semibold text-[#1F2933]">
          {cluster.label}
        </span>
        <Tooltip title={style.tip}>
          <span
            className={`inline-flex w-fit cursor-help items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${style.pill}`}
          >
            {AGREEMENT_LABEL[cluster.agreement]}
          </span>
        </Tooltip>
      </div>
      <div className="flex flex-1 flex-wrap items-center gap-1.5">
        {cluster.values.length === 0 ? (
          <span className="text-[12px] text-[#94A3B8]">
            Not recorded by any source
          </span>
        ) : (
          cluster.values.map((value) => (
            <ValueChip
              key={value.value}
              value={value}
              agreement={cluster.agreement}
            />
          ))
        )}
        {cluster.missing > 0 && cluster.values.length > 0 && (
          <Tooltip title="Raw records that left this field blank. Blanks are not treated as disagreement.">
            <span className="cursor-help text-[11px] font-semibold text-[#556C75]">
              {cluster.missing} blank
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

export default function FieldAgreementList({
  clusters,
}: FieldAgreementListProps) {
  return (
    <div className="flex flex-col">
      {clusters.map((cluster) => (
        <ClusterRow key={cluster.field} cluster={cluster} />
      ))}
    </div>
  );
}
